import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ArrowRightAlt, WestRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Link,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
} from "@mui/material";
import { useStytchB2BClient, useStytchMemberSession } from "@stytch/react/b2b";
import { useLocation, useNavigate } from "react-router-dom";
import { extractDomain } from "../_src_shared/utils/functions/extractDomain";
import { showWhenNotNil } from "../_src_shared/utils/functions/showWhenNotNil";
import DashboardLogo from "../assets/DashboardLogo";
import AnimatedCheckmark from "../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import InputWrapper from "../components/inputs/shared/InputWrapper";
import Flx from "../components/layout/Flx";
import Htag from "../components/typography/Htag";
import Txt from "../components/typography/Txt";

const CreateAccountScreen = () => {
  const navigate = useNavigate();

  const { session } = useStytchMemberSession();

  useEffect(() => {
    if (session) {
      navigate("/");
    }
  }, [session, navigate]);

  const location = useLocation();
  const stytch = useStytchB2BClient();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    accountType: "existing",
    organizationName: "",
    organizationSlug: "",
  });

  const steps = ["Account Details", "Organization", "Review"];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "organizationName") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({
        ...prev,
        organizationSlug: slug,
      }));
    }
  };

  const handleNext = () => {
    setError("");

    if (activeStep === 0) {
      if (!formData.email) {
        setError("Please enter your email address");
        return;
      }
    }

    if (activeStep === 1 && formData.accountType === "new") {
      if (!formData.organizationName) {
        setError("Please enter your organization name");
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError("");
    setActiveStep((prev) => prev - 1);
  };

  const onSubmit = async () => {
    const { email } = formData;
    if (!email) {
      setError("Please enter an email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await stytch.magicLinks.email.discovery.send({
        email_address: email,
        discovery_redirect_url: `${window.location.origin}/authenticate`,
        login_redirect_url: `${window.location.origin}/authenticate`,
      });

      if (response.status_code === 200) {
        setSuccess(true);
      } else {
        setError("Failed to send magic link. Please try again.");
      }
    } catch (err) {
      console.error("Error sending magic link:", err);
      setError("An error occurred while sending the magic link.");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Flx column gap={2} fw>
            <Flx gap={2} fw>
              <InputWrapper fullWidth label="First Name">
                <TextField
                  fullWidth
                  autoFocus
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </InputWrapper>
              <InputWrapper fullWidth label="Last Name">
                <TextField
                  fullWidth
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </InputWrapper>
            </Flx>
            <InputWrapper fullWidth label="Email Address">
              <TextField
                fullWidth
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </InputWrapper>
          </Flx>
        );

      case 1:
        return (
          <Flx column gap={2} fw>
            {formData.accountType === "new" && (
              <Flx column gap={2} fw>
                <InputWrapper fullWidth label="Organization Name">
                  <TextField
                    fullWidth
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    required
                    helperText={showWhenNotNil(
                      formData.organizationSlug,
                      `URL-friendly identifier: ${formData.organizationSlug}`
                    )}
                  />
                </InputWrapper>
              </Flx>
            )}

            {formData.accountType === "existing" && (
              <Alert severity="info">
                You'll be added to the appropriate organization based on your
                email domain, or placed in a temporary group until an admin
                assigns you to your organization.
                <br />
                <Flx gap={0.5} sx={{ mt: 2 }}>
                  <Txt fontSize={12} bold>
                    Requested Organization:
                  </Txt>
                  <Txt fontSize={12}>{extractDomain(formData?.email)}</Txt>
                </Flx>
              </Alert>
            )}
          </Flx>
        );

      case 2:
        return (
          <Box>
            <Flx column gap={1.5}>
              <Flx gap={0.5}>
                <Txt bold>Name:</Txt>
                <Txt>
                  {formData.firstName} {formData.lastName}
                </Txt>
              </Flx>

              <Flx gap={0.5}>
                <Txt bold>Email:</Txt>
                <Txt>{formData.email}</Txt>
              </Flx>

              <Flx gap={0.5}>
                <Txt bold>Organization:</Txt>
                <Txt>
                  {formData.accountType === "new"
                    ? formData.organizationName
                    : `${extractDomain(formData?.email)}`}
                </Txt>
              </Flx>
            </Flx>

            <Alert severity="info" sx={{ mt: 2 }}>
              We'll send a magic link to your email to complete the account
              creation process.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    setFormData((prev) => ({
      ...prev,
      firstName: params.get("firstName") || prev.firstName,
      lastName: params.get("lastName") || prev.lastName,
      email: params.get("email") || prev.email,
      orgId: params.get("orgId") || prev.orgId,
    }));
  }, [location.search]);

  if (success) {
    return (
      <Root>
        <Flx center column gap={2} sx={{ mb: 4 }}>
          <DashboardLogo width="58px" />
          <Htag h1>Account Created!</Htag>
          <Txt center sx={{ mb: 2 }}>
            A magic link has been sent to your email address. Please check your
            inbox to complete the account creation process and log in.
          </Txt>
        </Flx>
        <AnimatedCheckmark />
        <Flx gap={2} center sx={{ mt: 4 }}>
          <Flx center g={1}>
            <OutlookSvg />
            <Link
              underline="none"
              href="https://outlook.office.com/mail/0/inbox"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: "var(--ibm)",
                color: "#0072c6",
                fontWeight: 500,
                fontSize: "13px",
              }}
            >
              Open in Outlook
            </Link>
          </Flx>
          <Divider orientation="vertical" flexItem />
          <Link
            underline="none"
            onClick={() => navigate("/login")}
            role="button"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontFamily: "var(--ibm)",
              color: "#0072c6",
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            Go to Login Page
            <ArrowRightAlt />
          </Link>
        </Flx>
      </Root>
    );
  }

  return (
    <Root>
      <Flx center column gap={2} sx={{ mb: 4 }}>
        <DashboardLogo width="58px" />
        <Htag h1>Create Resilender Account</Htag>
      </Flx>

      <Stepper activeStep={activeStep} sx={{ mb: 4, width: "100%" }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, width: "100%" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, width: "100%" }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mt: 2, width: "100%" }}>{renderStepContent()}</Box>

      <Flx fw sx={{ mt: 6, justifyContent: "space-between" }}>
        {activeStep === 0 ? null : (
          <Tooltip title="Go back to the previous step">
            <IconButton
              variant="text"
              size="large"
              sx={{ position: "absolute", left: "-14px", top: "-14px" }}
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              <WestRounded />
            </IconButton>
          </Tooltip>
        )}

        {activeStep === steps.length - 1 ? (
          <Button
            size="large"
            variant="contained"
            onClick={onSubmit}
            disabled={loading}
            loading={loading}
            fullWidth
            sx={{ minWidth: "150px", height: "48px" }}
          >
            Create Account
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            size="large"
            sx={{ minWidth: "150px", height: "48px" }}
            fullWidth
          >
            Next
          </Button>
        )}
      </Flx>

      <Flx center sx={{ mt: 3 }}>
        <Txt center>
          Already have an account?{" "}
          <Link
            color="primary"
            onClick={() => navigate("/login")}
            sx={{
              textTransform: "none",
              ml: 0.5,
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            Sign In
          </Link>
        </Txt>
      </Flx>
    </Root>
  );
};

const Root = ({ children }) => {
  const width = useMemo(() => 600, []);
  const offset = useMemo(() => 100, []);

  return (
    <Flx
      center
      column
      sx={{
        p: 1,
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Card
        sx={{
          p: 4,
          py: 6,
          position: "relative",
          maxWidth: width,
          width: "100%",
        }}
      >
        <Flx
          fw
          center
          column
          sx={{
            position: "relative",
            maxWidth: width - offset,
            width: "100%",
            margin: "0 auto",
          }}
        >
          {children}
        </Flx>
      </Card>
    </Flx>
  );
};

Root.propTypes = {
  children: PropTypes.node.isRequired,
};

const OutlookSvg = ({ props }) => {
  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        id="Layer_1"
        style={{ width: "16px" }}
        viewBox="0 0 103.17322 104.31332"
        xmlSpace="preserve"
      >
        <defs id="defs43"></defs>
        <path
          d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z"
          id="path3"
          style={{ fill: "rgb(0, 114, 198)" }}
        ></path>
        <path
          d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z"
          id="path5"
          style={{ fill: "rgb(0, 114, 198)" }}
        ></path>
        <g
          id="g23"
          transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)"
        >
          <path
            d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z"
            id="path25"
            style={{ fill: "rgb(0, 114, 198)" }}
          ></path>
          <path
            d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z"
            id="path27"
            style={{ fill: "rgb(0, 114, 198)" }}
          ></path>
        </g>
      </svg>
    </>
  );
};

OutlookSvg.propTypes = {
  props: PropTypes.object,
};

export default CreateAccountScreen;
